import './CancelActionModal.scss';

import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { Button, Modal, ModalProps } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';

import Select from '../../../src/components/Select';
import warningGlowIcon from '../../assets/images/icons/warning-glow.svg';
import { CommonDropdownType } from '../../types';
import Textarea from '../Textarea';
import { useTranslation } from 'react-i18next';
import LoaderButton from '../common/LoaderButton';

interface CancelActionModalProps extends ModalProps {
  onConfirm?: (e?: SyntheticEvent, reason?: string, additionalDetails?: string) => void;
  onCancel?: (e?: SyntheticEvent) => void;
  title: string;
  helpText?: string;
  cancelText?: string;
  confirmText?: string;
  isFromWaitingRoom?: boolean;
  pickerOptions?: any;
  isSubmitting?: boolean;
}

const OTHER = 'other';

const CancelActionModal: FunctionComponent<CancelActionModalProps> = ({
  onConfirm,
  onCancel,
  title,
  helpText,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  isFromWaitingRoom = false,
  pickerOptions,
  isSubmitting,
  ...props
}) => {
  const { t }: any = useTranslation();
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);

  const { control, watch, reset } = useForm({
    defaultValues: {
      cancelationReason: undefined as CommonDropdownType | undefined,
      value: undefined as string | undefined,
      description: undefined as string | undefined,
    },
  });

  const cancelationReasonValue = watch('cancelationReason')?.value;
  const additionalDetails = watch('description');

  useEffect(() => {
    if (cancelationReasonValue !== undefined) {
      if (cancelationReasonValue === OTHER) {
        setIsConfirmDisabled(true);
        if (additionalDetails && additionalDetails.length > 5) {
          setIsConfirmDisabled(false);
        }
      } else {
        setIsConfirmDisabled(false);
      }
    }
  }, [cancelationReasonValue, additionalDetails]);

  const onHide = () => {
    reset();
    setIsConfirmDisabled(true);
    onCancel?.();
  };

  return (
    <Modal
      {...props}
      backdrop='static'
      keyboard={false}
      fullscreen={'md-down'}
      centered
      contentClassName='cancel-modal__content'
      dialogClassName='cancel-modal__dialog'
      onHide={onHide}
    >
      <Modal.Header closeButton className='cancel-modal__header'>
        <Modal.Title>
          <img src={warningGlowIcon} alt='' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='cancel-modal__body'>
        <h4 className='cancel-modal__title'>{title}</h4>
        <p>{helpText}</p>
        {isFromWaitingRoom && (
          <div>
            <h4 className='cancel-modal__reason_title'>{t('Select Reason for Cancellation')}</h4>
            <Controller
              name='cancelationReason'
              control={control}
              render={({ field: { ...rest } }) => (
                <Select
                  {...rest}
                  placeholder={'Select'}
                  id='cancelation-reason-select'
                  name='cancelation-reason'
                  options={pickerOptions}
                />
              )}
            />
          </div>
        )}

        {cancelationReasonValue === OTHER && (
          <div>
            <Controller
              name='description'
              control={control}
              render={({ field }) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { ref, ...nonRefField } = field;
                return (
                  <div>
                    <h4 className='cancel-modal__details_title'>Additional Details</h4>
                    <Textarea
                      id='service-new-description'
                      placeholder={'Enter a description'}
                      rows={5}
                      {...nonRefField}
                    />
                  </div>
                );
              }}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='cancel-modal__footer'>
        <Button
          onClick={(e) => {
            props.onHide?.();
            onCancel?.(e);
            reset();
          }}
          style={{ fontSize: '12px' }}
          variant={'secondary'}
          className='cancel-modal__btn'
          type={'button'}
          data-testid='cancel-action'
        >
          {cancelText}
        </Button>
        <LoaderButton
          onClick={(e) => {
            props.onHide?.();
            onConfirm?.(e, cancelationReasonValue, additionalDetails);
            reset();
          }}
          isSubmitting={isSubmitting}
          disabled={isFromWaitingRoom && isConfirmDisabled}
          style={{ fontSize: '12px' }}
          className='text-white cancel-modal__btn primary'
          type={'button'}
          data-testid='confirm-action'
        >
          {confirmText}
        </LoaderButton>
      </Modal.Footer>
    </Modal>
  );
};

export default CancelActionModal;
